import { useCallback, useState } from 'react'
import {
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  rectIntersection,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { useSnackbar } from '../../../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../../../components/stories/molecules/snackbar/constants'
import { useMovePlansAndFoldersMutation } from '../../../../../react-query/plan/mutation/use-move-plans-and-folders-mutation'
import { planMenuApiErrorCodeOptions } from '../../../constants'
import {
  getActiveItem,
  getExpandedWithFolder,
  getItemsToMove,
  getMovableItemsToFolder,
  getMovableItemsToRoot,
  getMovePayload,
  menuTreeDropTargetTypeOptions
} from '../../../../../components/menu-tree/utils'

const getFolderCollisions = (collisions) =>
  collisions.filter(
    (collision) =>
      collision.data?.droppableContainer?.data.current?.type ===
      menuTreeDropTargetTypeOptions.FOLDER
  )

const getRootRowCollisions = (collisions) =>
  collisions.filter(
    (collision) =>
      collision.data?.droppableContainer?.data.current?.type ===
      menuTreeDropTargetTypeOptions.ROOT_ROW
  )

const getRowCollisions = (collisions) =>
  collisions.filter(
    (collision) =>
      collision.data?.droppableContainer?.data.current?.type ===
      menuTreeDropTargetTypeOptions.ROW
  )

// the drag surface is the whole row, so the row's own controls must stay
// clickable: the pointer has to travel a few pixels, or the touch has to dwell,
// before dnd-kit takes the interaction over
const mouseActivationConstraint = { distance: 5 }
const touchActivationConstraint = { delay: 250, tolerance: 5 }

// cells that own a control of their own (menus, checkbox) carry this attribute,
// and the sensors below ignore activations coming from inside them. The cell
// can't just stop the event: that also hides it from the document, where the
// context menus listen for the outside mousedown that closes them
export const planMenuPreventDragAttribute = 'data-prevent-drag'

const isFromPreventDragCell = (event) =>
  !!event.nativeEvent.target.closest?.(`[${planMenuPreventDragAttribute}]`)

const withPreventDragCells = (activators) =>
  activators.map(({ eventName, handler }) => ({
    eventName,
    handler: (event, options) =>
      !isFromPreventDragCell(event) && handler(event, options)
  }))

class PlanMenuMouseSensor extends MouseSensor {
  static activators = withPreventDragCells(MouseSensor.activators)
}

class PlanMenuTouchSensor extends TouchSensor {
  static activators = withPreventDragCells(TouchSensor.activators)
}

const isConflictError = (error) =>
  error?.response?.data?.status ===
    planMenuApiErrorCodeOptions.PLAN_NAME_IS_NOT_AVAILABLE ||
  error?.response?.status ===
    planMenuApiErrorCodeOptions.PLAN_NAME_IS_NOT_AVAILABLE

export const usePlanMenuDnd = ({
  formatMessage,
  projectId,
  rowSelection,
  rowsById,
  setExpanded
}) => {
  const { addSnackbar } = useSnackbar()
  const [movingItemIds, setMovingItemIds] = useState([])

  const { mutate: moveItems, isPending: isMovingItems } =
    useMovePlansAndFoldersMutation({
      onSuccess: () => {
        addSnackbar({
          message: formatMessage({
            id: 'moveItems_items_moved',
            defaultMessage: 'Items moved'
          }),
          snackbarType: snackbarVariantOptions.SUCCESS,
          hideCloseButton: true
        })
      },
      onError: (error, variables) => {
        setMovingItemIds([])

        const messageId = isConflictError(error)
          ? variables?.planIds?.length
            ? 'planMenu_plan_with_this_name_aready_exists'
            : 'projectMenu_folder_with_this_name_aready_exists'
          : 'moveItems_move_error'

        addSnackbar({
          message: formatMessage({ id: messageId }),
          snackbarType: snackbarVariantOptions.ERROR,
          hideCloseButton: true,
          timeToHide: 5000
        })
      },
      onSettled: () => {
        setMovingItemIds([])
      }
    })

  const sensors = useSensors(
    useSensor(PlanMenuMouseSensor, {
      activationConstraint: mouseActivationConstraint
    }),
    useSensor(PlanMenuTouchSensor, {
      activationConstraint: touchActivationConstraint
    }),
    useSensor(KeyboardSensor, {})
  )

  const collisionDetection = useCallback((args) => {
    const collisions = rectIntersection(args)
    const folderCollisions = getFolderCollisions(collisions)
    const rootRowCollisions = getRootRowCollisions(collisions)
    const rowCollisions = getRowCollisions(collisions)

    if (folderCollisions.length) return folderCollisions
    if (rootRowCollisions.length) return rootRowCollisions
    if (rowCollisions.length) return rowCollisions

    return collisions
  }, [])

  const openFolder = useCallback(
    (folderId) => {
      setExpanded((currentExpanded) =>
        getExpandedWithFolder(currentExpanded, folderId)
      )
    },
    [setExpanded]
  )

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event
      const dropTargetType = over?.data.current?.type
      const destinationFolder =
        dropTargetType === menuTreeDropTargetTypeOptions.FOLDER
          ? over.data.current.folder
          : null
      const isRootDrop = [
        menuTreeDropTargetTypeOptions.ROOT,
        menuTreeDropTargetTypeOptions.ROOT_ROW
      ].includes(dropTargetType)
      const activeItem = getActiveItem(active, rowsById)

      if (!activeItem || (!destinationFolder && !isRootDrop)) return

      const itemsToMove = getItemsToMove({ activeItem, rowSelection, rowsById })
      const movableItems = destinationFolder
        ? getMovableItemsToFolder(itemsToMove, destinationFolder)
        : getMovableItemsToRoot(itemsToMove)

      if (!movableItems.length) return

      if (destinationFolder) {
        openFolder(destinationFolder.id)
      }

      setMovingItemIds(movableItems.map(({ id }) => String(id)))

      moveItems({
        ...getMovePayload(movableItems, 'planIds'),
        destinationFolderId: destinationFolder?.id ?? null,
        projectId
      })
    },
    [moveItems, openFolder, projectId, rowSelection, rowsById]
  )

  return {
    collisionDetection,
    handleDragEnd,
    isMovingItems,
    movingItemIds,
    sensors
  }
}
